"use strict";
/**
 * @file locales.ts
 *
 * The platform's locale registry and resolution helpers — the single source of
 * truth for which locales the apps support and how an arbitrary language tag is
 * mapped onto one of them. The list must stay in step with the Phrase project's
 * locales and the message catalogs each app ships.
 *
 * Matching is hand-rolled (no runtime dependency) and intentionally simple:
 * exact tag, then language-only fallback, then the default.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.LOCALE_CLS_KEY = exports.DEFAULT_LOCALE = exports.SUPPORTED_LOCALES = void 0;
exports.normalizeLocale = normalizeLocale;
exports.isSupportedLocale = isSupportedLocale;
exports.resolveLocale = resolveLocale;
exports.negotiateLocale = negotiateLocale;
/**
 * Every locale the platform supports, as BCP-47 tags. The first entry is the
 * fallback used whenever a requested locale can't be matched.
 */
exports.SUPPORTED_LOCALES = [
    'en-GB',
    'da-DK',
    'de-DE',
    'es-ES',
    'fi-FI',
    'fr-FR',
    'it-IT',
    'nl-NL',
    'nb-NO',
    'pt-PT',
    'sv-SE',
];
/** Locale used when nothing better can be resolved. */
exports.DEFAULT_LOCALE = 'en-GB';
/** CLS store key under which the negotiated request locale is kept. */
exports.LOCALE_CLS_KEY = 'locale';
/** First supported locale for each base language, e.g. `de` → `de-DE`. */
const languageIndex = new Map();
for (const locale of exports.SUPPORTED_LOCALES) {
    const language = locale.split('-')[0];
    if (!languageIndex.has(language))
        languageIndex.set(language, locale);
}
/**
 * Normalizes separator and casing quirks (e.g. `en_us` → `en-US`).
 *
 * @param input - A locale tag in any separator/casing variant.
 *
 * @returns The tag with a `-` separator, lowercase language and uppercase region.
 */
function normalizeLocale(input) {
    const [language, region] = input.replace('_', '-').split('-');
    return region
        ? `${language.toLowerCase()}-${region.toUpperCase()}`
        : language.toLowerCase();
}
/**
 * Type guard for the supported-locale set.
 *
 * @param locale - The tag to test.
 *
 * @returns `true` when `locale` is one of {@link SUPPORTED_LOCALES}.
 */
function isSupportedLocale(locale) {
    return exports.SUPPORTED_LOCALES.includes(locale);
}
/**
 * Closest supported locale for a single tag.
 *
 * @param tag - A single language tag.
 *
 * @returns The matching supported locale, or `undefined` if none matches.
 */
function matchLocale(tag) {
    const normalized = normalizeLocale(tag);
    if (isSupportedLocale(normalized))
        return normalized;
    return languageIndex.get(normalized.split('-')[0]);
}
/**
 * Resolves a single language tag to the closest supported locale, falling back
 * to {@link DEFAULT_LOCALE}. Handles exact matches, casing/separator variants,
 * and language-only tags (e.g. `de` → `de-DE`).
 *
 * @param input - The tag to resolve; nullish resolves to the default.
 *
 * @returns The closest supported locale, or {@link DEFAULT_LOCALE}.
 */
function resolveLocale(input) {
    if (!input)
        return exports.DEFAULT_LOCALE;
    return matchLocale(input) ?? exports.DEFAULT_LOCALE;
}
/**
 * Negotiates the best supported locale from an HTTP `Accept-Language` header
 * value, honouring its quality-ordered list, and falls back to
 * {@link DEFAULT_LOCALE}.
 *
 * @param acceptLanguage - The raw `Accept-Language` header value, if any.
 *
 * @returns The best supported locale, or {@link DEFAULT_LOCALE}.
 */
function negotiateLocale(acceptLanguage) {
    if (!acceptLanguage)
        return exports.DEFAULT_LOCALE;
    const ranked = acceptLanguage
        .split(',')
        .map((part) => {
        const [tag, ...params] = part.trim().split(';');
        const quality = params
            .map((p) => p.trim())
            .find((p) => p.startsWith('q='));
        const q = quality ? Number.parseFloat(quality.slice(2)) : 1;
        return { tag: tag.trim(), q: Number.isFinite(q) ? q : 1 };
    })
        .filter((entry) => entry.tag && entry.tag !== '*')
        .sort((a, b) => b.q - a.q);
    for (const { tag } of ranked) {
        const match = matchLocale(tag);
        if (match)
            return match;
    }
    return exports.DEFAULT_LOCALE;
}
